/**
 * Etiquetas en español para valores de chatflows compartidas entre la lista y el formulario:
 * niveles de prioridad de resolución y categorías de plantilla.
 */

export interface ChatflowPriorityLevel {
  value: number;
  label: string;
}

/** Niveles canónicos de prioridad de resolución (menor valor = mayor prioridad). */
export const CHATFLOW_PRIORITY_LEVELS: ChatflowPriorityLevel[] = [
  { value: 10, label: 'Crítica' },
  { value: 40, label: 'Alta' },
  { value: 70, label: 'Media' },
  { value: 100, label: 'Baja' }
];

/** Etiqueta por rango: <=25 Crítica, <=50 Alta, <=75 Media, resto Baja. */
export function getChatflowPriorityLabel(priority: number): string {
  if (priority <= 25) return 'Crítica';
  if (priority <= 50) return 'Alta';
  if (priority <= 75) return 'Media';
  return 'Baja';
}

/** Normaliza cualquier valor guardado al nivel canónico que le corresponde por rango. */
export function normalizeChatflowPriority(priority: number): number {
  if (priority <= 25) return 10;
  if (priority <= 50) return 40;
  if (priority <= 75) return 70;
  return 100;
}

const CATEGORY_LABELS: Record<string, string> = {
  LEAD_QUALIFICATION: 'Calificación de leads',
  TECH_SUPPORT: 'Soporte técnico',
  POST_SERVICE_FEEDBACK: 'Feedback post-servicio',
  MEETING_BOOKING: 'Reserva de reuniones',
  ORDER_STATUS: 'Estado de pedidos',
  FAQ_AUTOMATION: 'Automatización de FAQs',
  GENERAL: 'General'
};

/** Traduce la categoría de plantilla; si no está mapeada devuelve el valor tal cual. */
export function getChatflowCategoryLabel(category?: string): string {
  if (!category) return '';
  return CATEGORY_LABELS[category] ?? category;
}

export interface ChatflowCategoryOption {
  value: string;
  label: string;
}

/** Opciones de categoría de plantilla para selects (valor backend + label en español). */
export const CHATFLOW_TEMPLATE_CATEGORIES: ChatflowCategoryOption[] =
  Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }));

const PARTICIPANT_TYPE_LABELS: Record<string, string> = {
  BOT: 'Bot',
  VISITOR: 'Visitante',
  AGENT: 'Agente',
  SYSTEM: 'Sistema'
};

/**
 * Etiqueta legible del emisor de un mensaje de transcript (simulador / runtime).
 * INC_CRM_019 #6: nunca concatenar `messageType` — el crudo tipo "BOT-TEXT" /
 * "VISITOR · QUICK_REPLY" no es apto para mostrar al usuario final.
 */
export function getChatflowParticipantLabel(participantType?: string | null): string {
  if (!participantType) return '';
  return PARTICIPANT_TYPE_LABELS[participantType] ?? participantType;
}
